/**
 * BankingFlowRecap — a tiny, dismissible "how the 4 banking tools fit
 * together" map that renders under the main PageInfoCallout on every
 * banking page. Helps users who land on page #2 of 4 understand what
 * the other 3 are for and in what order to use them (iter380).
 */
import { useState, useEffect } from 'react';
import { Map, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STEPS = [
  { to: '/banking-hub',           label: 'Banking Hub',          note: 'Daily overview · see what needs attention',       tint: 'violet'   },
  { to: '/banking-quick-link',    label: 'Quick-link Inbox',     note: 'Match un-linked bank lines to sales/expenses',    tint: 'sky'      },
  { to: '/banking-rules',         label: 'Auto-Rules',           note: 'Teach once — recurring lines auto-categorise',    tint: 'emerald'  },
  { to: '/pass-through-advances', label: 'Pass-Through Advances', note: 'Money in your account that isn\'t really yours',  tint: 'amber'    },
];

const TINT = {
  violet:  'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
  sky:     'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  amber:   'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
};

export default function BankingFlowRecap({ active }) {
  const [open, setOpen] = useState(true);
  const KEY = 'banking-flow-recap-dismissed';
  useEffect(() => {
    try { if (localStorage.getItem(KEY) === '1') setOpen(false); } catch { /* ignore */ }
  }, []);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); try { localStorage.removeItem(KEY); } catch { /* ignore */ } }}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        data-testid="banking-flow-reopen"
      >
        <Map size={12} /> Show banking tools map
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 p-3" data-testid="banking-flow-recap">
      <div className="flex items-start gap-2">
        <Map size={16} className="text-muted-foreground mt-0.5" />
        <div className="flex-1">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">How the 4 banking tools fit together</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {STEPS.map((s, i) => {
              const isActive = s.to === active;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  className={`block rounded-lg border px-2.5 py-2 text-left transition-all ${TINT[s.tint]} ${isActive ? 'ring-2 ring-offset-1 ring-current' : ''}`}
                  data-testid={`banking-flow-step-${i + 1}`}
                >
                  <div className="text-[10px] font-bold opacity-70">STEP {i + 1}</div>
                  <div className="text-sm font-semibold">{s.label}</div>
                  <div className="text-[11px] opacity-80 leading-tight">{s.note}</div>
                </Link>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ } }}
          className="text-muted-foreground/60 hover:text-foreground"
          aria-label="Dismiss"
          data-testid="banking-flow-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
